import React, { useState, useEffect } from 'react';

import Form from 'react-bootstrap/Form';
import { TiInfoLarge } from 'react-icons/ti';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import FunsiteResidueSet from '../../models/FunsiteResidueSet';

const FUNSITE_MIN_CONFIDENCE = 0.8

const FUNSITE_TYPE_BIOLIP_LIG = {
    id: 'BIOLIP_LIG',
    name: 'BioLiP (Lig)',
    color: 'green',
    desc: 'BioLiP Protein Function Database',
}

const FUNSITE_TYPE_BIOLIP_NUC = {
    id: 'BIOLIP_NUC',
    name: 'BioLiP (Nuc)',
    color: 'purple',
    desc: 'BioLiP Protein Function Database',
}

const FUNSITE_TYPE_MCSA = {
    id: 'MCSA',
    name: 'M-CSA',
    color: 'red',
    desc: 'Enzyme active sites from Catalytic Site Atlas',
}

const FUNSITE_TYPE_IBIS_PPI = {
    id: 'ibis_ppi',
    name: 'IBIS PPI',
    color: 'orange',
    desc: 'Protein-Protein Interactions from IBIS (Inferred Biomolecular Interaction Server)',
}

const FUNSITE_TYPE_SCORECONS = {
    id: 'SCORECONS',
    name: 'Scorecons',
    color: 'blue',
    desc: 'Highly conserved sequence positions from CATH Functional Families (FunFams)',
}

const FUNSITE_TYPES = [
    FUNSITE_TYPE_MCSA,
    FUNSITE_TYPE_SCORECONS,
    FUNSITE_TYPE_BIOLIP_LIG,
    FUNSITE_TYPE_BIOLIP_NUC,
    FUNSITE_TYPE_IBIS_PPI
]

function FunsiteControls(props) {

    const { cathVersion, repId } = props

    const [funsites, setFunsites] = useState(null);
    const [funsitesError, setFunsitesError] = useState(null);
    const [funsitesIsLoading, setFunsitesIsLoading] = useState(true);

    let defaultEnabledFunsiteTypes = {}
    for (const fsType of FUNSITE_TYPES) {
        defaultEnabledFunsiteTypes[fsType.id] = false
    }

    const [enabledFunsiteTypes, setEnabledFunsiteTypes] = useState(defaultEnabledFunsiteTypes)

    const { onChangeFunsiteResidueSets } = props

    const toggleFunsiteType = (funsiteType) => {
        // console.log("toggleFunsiteType", funsiteType)
        enabledFunsiteTypes[funsiteType] = !enabledFunsiteTypes[funsiteType]
        setEnabledFunsiteTypes(enabledFunsiteTypes)
        updateFunsiteResidueSets()
    }

    const updateFunsiteResidueSets = () => {
        const funsitesByType = getFunsitesByType()
        let residueSets = []
        for (const funsiteType of FUNSITE_TYPES) {
            const funsiteId = funsiteType.id
            // console.log("updateFunsiteResidueSets", funsiteId, funsitesByType[funsiteId], enabledFunsiteTypes)
            if (enabledFunsiteTypes[funsiteId] === true) {
                const fsList = funsitesByType[funsiteId]
                const residues = fsList.map((fs) => fs.res)
                const residueSet = new FunsiteResidueSet(funsiteType.id, funsiteType.color, funsiteType.name, residues)
                residueSets.push(residueSet)
            }
        }
        // console.log("onChangeFunsiteResidueSets", residueSets)
        onChangeFunsiteResidueSets(residueSets)
    }

    const getFunsitesByType = () => {
        const funsites_by_type = {}
        for (const fsType of FUNSITE_TYPES) {
            funsites_by_type[fsType.id] = []
        }
        // console.log("funsites_by_type", funsites_by_type)
        for (const fs of funsites) {
            if (!(fs.type in funsites_by_type)) {
                console.error("unexpected funsite type: ", fs.type)
                continue
            }
            funsites_by_type[fs.type].push(fs)
        }
        return funsites_by_type
    }

    useEffect(() => {
        const funsiteUrl = `/api/funsite/version/${cathVersion}/rep/${repId}/summary`

        if (!funsites) {
            setFunsitesIsLoading(true);
            fetch(funsiteUrl)
                .then((res) => {
                    if (!res.ok) {
                        const msg = 'funsite response not ok'
                        throw new Error(msg)
                    }
                    return res.json()
                })
                .then(response => {
                    const repId = response.rep_id;
                    const funfamId = response.funfam_id;
                    // console.log("setFunsites", response.funsites);
                    const filteredFunsites = response.funsites.filter(fs => {
                        return fs.conf >= FUNSITE_MIN_CONFIDENCE
                    })
                    setFunsites(filteredFunsites);
                    setFunsitesIsLoading(false);
                })
                .catch(error => {
                    console.log(error);
                    setFunsitesError(error);
                });
        }


    }, [repId, cathVersion, enabledFunsiteTypes]);

    let funsite_controls;
    if (funsitesError) {
        funsite_controls = `Error: ${funsitesError.message}`;
    }
    else if (funsitesIsLoading) {
        funsite_controls = 'Loading...'
    }
    else {
        let funsites_by_type = getFunsitesByType()

        funsite_controls = (
            <Form inline>
                {FUNSITE_TYPES.map((fs_type) => {
                    // console.log("render funsite: ", fs_type, funsites_by_type[fs_type.id])
                    const fs_id = `funsite-${fs_type.id}`
                    const fs_data = funsites_by_type[fs_type.id]
                    const fs_label = `${fs_type.name} (${fs_data.length})`
                    const fs_checked = enabledFunsiteTypes[fs_type.id]
                    return (
                        <OverlayTrigger
                            key={fs_id}
                            placement="top"
                            overlay={<Tooltip>{fs_type.desc}</Tooltip>}
                        >
                            {({ ref, ...triggerHandler }) => (
                                <div className="mb-3" {...triggerHandler} ref={ref}>
                                    <Form.Check
                                        inline
                                        disabled={fs_data.length > 0 ? false : true}
                                        type="switch"
                                        id={fs_id}
                                        checked={fs_checked}
                                        onChange={() => { toggleFunsiteType(fs_type.id) }}
                                        label={fs_label}>
                                    </Form.Check>
                                </div>
                            )}
                        </OverlayTrigger>
                    )
                })}
            </Form>
        )
    }

    return (
        <div>
            {funsite_controls}
        </div>
    );
}

export default FunsiteControls;